<template>
  <form
    method="POST"
    class="form"
    :class="modifiers"
    v-bind="$attrs"
    @submit.prevent="submit"
  >
    <div v-if="fields.length" class="form__fields">
      <div
        v-for="item in fields"
        :key="`form_field_${item.id}`"
        class="form__field"
      >
        <component
          :ref="`field_${item.id}`"
          :is="item.component || 'BaseField'"
          :value="values[item.id]"
          v-bind="item.field"
          @input="changeFormField(item, $event)"
        />
      </div>
    </div>
    <slot />
    <div class="form__actions">
      <i18n v-if="hasTermsText" path="texts.termsFull" tag="div" class="form__terms">
        <button
          type="button"
          class="terms-link"
          @click="$emit('show:rules')"
        >
          <mark>{{ $t('texts.termsRefund') }}</mark>
        </button>
        <button
          type="button"
          class="terms-link"
          @click="$emit('show:terms')"
        >
          <mark>{{ $t('texts.termsPersonal') }}</mark>
        </button>
      </i18n>
      <div v-if="hasTermsLink" class="form__terms">
        <button
          type="button"
          class="terms-link"
          @click="$emit('show:terms')"
        >
          <mark>{{ $t('actions.termsLink') }}</mark>
        </button>
      </div>
      <BaseButton
        v-if="hasHomeLink"
        :href="$router.options.paths.home"
        :text="$t('actions.home')"
      />
      <BaseButton
        :text="submitButtonText"
        type="submit"
      />
      <slot name="actions" />
    </div>
  </form>
</template>

<script>
import BaseField from '@/components/BaseField.vue';
import BaseButton from '@/components/BaseButton.vue';

export default {
  name: 'BaseForm',
  components: {
    BaseField,
    BaseButton,
  },
  props: {
    fields: {
      type: Array,
      default: () => [],
    },
    submitButtonText: {
      type: String,
      default() {
        return this.$t('actions.send');
      },
    },
    hasHomeLink: {
      type: Boolean,
      default: false,
    },
    hasTermsLink: {
      type: Boolean,
      default: false,
    },
    hasTermsText: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      values: {},
    };
  },
  computed: {
    modifiers() {
      return {
        form_has_home: this.hasHomeLink,
        form_has_terms: this.hasTermsLink || this.hasTermsText,
      };
    },
  },
  created() {
    this.setDefaultValues();
  },
  methods: {
    changeFormField(field, value) {
      this.setFieldValue(field.id, value);
    },
    setFieldValue(id, value) {
      this.$set(this.values, id, value);
    },
    setFieldFocusById(id) {
      const [firstComponent] = this.$refs[`field_${id}`];
      return firstComponent?.focus?.();
    },
    setFocus() {
      const [firstField] = this.fields;
      return this.setFieldFocusById(firstField?.id);
    },
    setDefaultValues() {
      this.fields.forEach((field) => this.setFieldValue(field.id, field.defaultValue));
    },
    checkFormComplete() {
      return this.fields.every((field) => {
        const [firstComponent] = this.$refs[`field_${field.id}`];

        if (firstComponent.checkValidity()) {
          return true;
        }

        this.setFieldFocusById(field.id);

        return false;
      });
    },
    submit() {
      // При отправке формы лучше сбросить фокус у активного поля
      document.activeElement.blur();

      if (this.checkFormComplete()) {
        this.$emit('submit', this.values);
      }
    },
  },
};
</script>

<style lang="stylus">
.form
  display block

  &__field
    margin-top 0.7em
    &:first-child
      margin-top 0

  &__actions
    display flex
    align-items center
    justify-content center
    margin-top 1.5em
    ~/_has_terms &
      justify-content flex-end
      .button
        margin 0
    .button
      margin-left 0.4em
      margin-right @margin-left

  &__terms
    font-size 0.75em
    line-height 1.4
    margin 0 auto 0 0
    padding-right 4em
</style>
